import styles from './styles.module.scss';
import cn from 'classnames';
import { ReactNode } from 'react';

export interface SelectableTableColumn {
  header?: ReactNode;
  align?: 'left' | 'right';
  width?: string;
}

export interface SelectableTableRow {
  id: string | number;
  cells: ReactNode[];
  disabled?: boolean;
}

export type SelectionMode = 'single' | 'multiple' | 'none';

export interface SelectableTableProps {
  /** Строки: id и массив ячеек (количество должно совпадать с columns или быть 1 для списка) */
  rows: SelectableTableRow[];
  /** Опциональные колонки: заголовки и выравнивание. Если не заданы — заголовков нет */
  columns?: SelectableTableColumn[];
  /** Режим выбора: один (radio), несколько (checkbox) или без выбора */
  selectionMode?: SelectionMode;
  /** Выбранный id (для single) */
  selectedId?: string | number | null;
  /** Выбранные id (для multiple) */
  selectedIds?: (string | number)[];
  /** Обработчик смены выбора: для single — один id, для multiple — массив id */
  onSelectionChange?: (idOrIds: string | number | (string | number)[]) => void;
  /** Подсветка активной строки (например, фокус) */
  activeRowId?: string | number | null;
  /** Подсветка строки при наведении */
  hoverable?: boolean;
  /** Ограничение высоты: при превышении скролл только по tbody, заголовки остаются на месте (например "400px" или 400) */
  maxHeight?: string | number;
  /** Фиксировать заголовки при скролле */
  stickyHeader?: boolean;
  className?: string;
  /** Дополнительный класс для строки */
  rowClassName?: (row: SelectableTableRow, index: number) => string | undefined;
}

const formCheckClass = 'form-check form-check-custom form-check-solid form-check-secondary';

export const SelectableTable = ({
  rows,
  columns = [],
  selectionMode = 'none',
  selectedId = null,
  selectedIds = [],
  onSelectionChange,
  activeRowId = null,
  hoverable = true,
  maxHeight,
  stickyHeader = false,
  className,
  rowClassName
}: SelectableTableProps) => {
  const hasHeaders = columns.length > 0 && columns.some((c) => c.header != null);
  const cellCount = columns.length > 0 ? columns.length : rows[0]?.cells?.length ?? 1;

  const isRowSelected = (row: SelectableTableRow) => {
    if (selectionMode === 'single') return selectedId != null && row.id === selectedId;
    if (selectionMode === 'multiple') return selectedIds.includes(row.id);
    return false;
  };

  const handleSingleChange = (rowId: string | number) => {
    if (selectionMode !== 'single' || onSelectionChange == null) return;
    onSelectionChange(rowId);
  };

  const handleMultipleChange = (rowId: string | number, checked: boolean) => {
    if (selectionMode !== 'multiple' || onSelectionChange == null) return;
    const next = checked ? [...selectedIds, rowId] : selectedIds.filter((id) => id !== rowId);
    onSelectionChange(next);
  };

  const scrollInsideTbody = maxHeight != null && hasHeaders;
  const maxHeightValue = maxHeight != null ? (typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight) : undefined;

  const renderSelectionControl = (row: SelectableTableRow, selected: boolean) => {
    if (selectionMode === 'none') return null;

    if (selectionMode === 'single') {
      return (
        <div className={formCheckClass} onClick={(e) => e.stopPropagation()}>
          <input
            type="radio"
            className="form-check-input"
            name="selectable-table-single"
            checked={selected}
            onChange={() => !row.disabled && handleSingleChange(row.id)}
            disabled={row.disabled}
          />
        </div>
      );
    }

    return (
      <div className={formCheckClass} onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          className="form-check-input"
          checked={selected}
          onChange={(e) => !row.disabled && handleMultipleChange(row.id, e.target.checked)}
          disabled={row.disabled}
        />
      </div>
    );
  };

  const tableEl = (
    <table
      className={cn(
        'table table-row-dashed table-row-gray-200 align-middle gs-0 gy-0 mb-0 w-100',
        styles.tableBordersHorizontal,
        !hasHeaders && styles.tableNoHeader,
        (maxHeight != null || stickyHeader) && hasHeaders && styles.tableStickyHead,
        scrollInsideTbody && styles.tableScrollInsideTbody,
        className
      )}
    >
      {hasHeaders && (
        <thead>
          <tr className="border-0">
            {columns.map((col, i) => (
              <th
                key={i}
                className={cn(
                  'fw-500 fs-7 text-gray-500 py-1 ps-2 pe-2',
                  col.align === 'right' ? 'text-end' : 'text-start',
                  selectionMode !== 'none' && i === 0 && styles.headerWithSelection
                )}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody style={scrollInsideTbody && maxHeightValue ? { maxHeight: maxHeightValue } : undefined}>
        {rows.map((row, rowIndex) => {
          const selected = isRowSelected(row);
          const isActive = activeRowId != null && row.id === activeRowId;
          const trClass = cn(
            'fs-7',
            isActive && 'bg-primary bg-opacity-10',
            hoverable && 'cursor-pointer',
            rowClassName?.(row, rowIndex)
          );

          const cells = row.cells.slice(0, cellCount);
          while (cells.length < cellCount) cells.push(null);

          return (
            <tr
              key={row.id}
              className={trClass}
              onClick={selectionMode === 'single' && !row.disabled ? () => handleSingleChange(row.id) : undefined}
            >
              {cells.map((cell, cellIndex) => {
                const col = columns[cellIndex];
                const align = col?.align === 'right' ? 'text-end' : 'text-start';
                const isFirstCell = cellIndex === 0;
                const padClass = 'ps-2 pe-2';
                return (
                  <td
                    key={cellIndex}
                    className={cn('py-1', align, padClass, 'fw-bold text-gray-500')}
                    style={col?.width ? { width: col.width } : undefined}
                  >
                    {selectionMode !== 'none' && isFirstCell ? (
                      <div className={styles.firstCellWithSelection}>
                        <div className={styles.selectionInline}>{renderSelectionControl(row, selected)}</div>
                        <div className={styles.firstCellContent}>{cell}</div>
                      </div>
                    ) : (
                      cell
                    )}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  if (maxHeight != null && !scrollInsideTbody) {
    return (
      <div className={styles.tableScrollWrap} style={{ maxHeight: maxHeightValue }}>
        {tableEl}
      </div>
    );
  }

  return tableEl;
};
